"use client";

import { useMemo, useState } from "react";
import type { BriefSummary } from "@/types/DailyBrief";
import dictionary from "@/utils/dictionary";
import BriefCard from "./BriefCard";
import styles from "./brief.module.scss";

import Link from "next/link";
import { archivePath, BRIEF_PAGE_SIZE as PAGE_SIZE } from "@/lib/briefs/archive";

export default function BriefArchive({ briefs, initialPage = 1 }: { briefs: BriefSummary[]; initialPage?: number }) {
    const copy = dictionary.brief;
    const [query, setQuery] = useState("");
    const [tag, setTag] = useState("");
    const [page, setPage] = useState(1);
    const tags = useMemo(() => [...new Set(briefs.flatMap((brief) => brief.tags))].sort(), [briefs]);
    const filtered = useMemo(
        () =>
            briefs.filter(
                (brief) =>
                    (!tag || brief.tags.includes(tag)) &&
                    `${brief.title} ${brief.summary} ${brief.date} ${brief.tags.join(" ")} ${brief.headlines.join(" ")}`
                        .toLowerCase()
                        .includes(query.trim().toLowerCase())
            ),
        [briefs, query, tag]
    );
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const filtering = !!(query.trim() || tag);
    const currentPage = filtering ? page : initialPage;
    const reset = () => {
        setQuery("");
        setTag("");
        setPage(1);
    };
    return (
        <section id="archive" className={styles.archive} aria-labelledby="archive-title">
            <div className={styles.section_heading}>
                <h2 id="archive-title">{copy.archive}</h2>
                <span role="status" aria-live="polite">
                    {filtered.length} {filtered.length === 1 ? copy.issue : copy.issues}
                </span>
            </div>
            <div className={styles.filters}>
                <label className={styles.search}>
                    <span>{copy.search}</span>
                    <input
                        type="search"
                        value={query}
                        placeholder={copy.search_placeholder}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setPage(1);
                        }}
                    />
                </label>
                <div className={styles.chips} role="group" aria-label={copy.topics}>
                    {["", ...tags].map((value) => (
                        <button
                            key={value}
                            type="button"
                            aria-pressed={tag === value}
                            onClick={() => {
                                setTag(value);
                                setPage(1);
                            }}
                        >
                            {value || copy.all}
                        </button>
                    ))}
                </div>
            </div>
            {filtered.length ? (
                <div className={styles.grid}>
                    {filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((brief) => (
                        <BriefCard brief={brief} key={brief.slug} />
                    ))}
                </div>
            ) : (
                <div className={styles.empty}>
                    <p>{briefs.length ? copy.empty : copy.no_issues}</p>
                    {(query || tag) && (
                        <button type="button" onClick={reset}>
                            {copy.reset}
                        </button>
                    )}
                </div>
            )}
            {pages > 1 && (
                <nav className={styles.pagination} aria-label={copy.archive}>
                    {!filtering && currentPage > 1 ? (
                        <Link href={`${archivePath(currentPage - 1)}#archive`}>{copy.newer}</Link>
                    ) : (
                        <button
                            type="button"
                            disabled={currentPage === 1}
                            onClick={() => setPage((value) => value - 1)}
                        >
                            {copy.newer}
                        </button>
                    )}
                    <span>
                        {copy.page} {currentPage} / {pages}
                    </span>
                    {!filtering && currentPage < pages ? (
                        <Link href={`${archivePath(currentPage + 1)}#archive`}>{copy.older}</Link>
                    ) : (
                        <button
                            type="button"
                            disabled={currentPage === pages}
                            onClick={() => setPage((value) => value + 1)}
                        >
                            {copy.older}
                        </button>
                    )}
                </nav>
            )}
        </section>
    );
}
